/**
 * Панель редактирования сцены §67.
 *
 * Правка показывает, что придётся пересоздать и сколько это будет стоить —
 * до подтверждения, а не после (§36).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SceneType,
  graphicsSpecSchema,
  sceneTypeLabels,
  type CaptionLine,
  type GraphicsSpec,
  type LookView,
  type SceneView,
} from '@alterium/shared';
import { projectsApi } from '../../api/projects';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CaptionsEditor } from './CaptionsEditor';
import { GraphicsEditor } from './GraphicsEditor';
import { SceneResult } from './SceneResult';
import { useTranslation } from 'react-i18next';

const editableTypes: SceneType[] = [
  SceneType.PERSONA_SPEECH,
  SceneType.DYNAMIC_PERSONA,
  SceneType.BROLL,
  SceneType.IMAGE,
  SceneType.MOTION_GRAPHICS,
];

interface ScenePanelProps {
  scene: SceneView;
  looks: LookView[];
  saving: boolean;
  /** `regenerate` — сразу пересоздать сцену после сохранения. */
  onSave: (patch: Record<string, unknown>, options?: { regenerate?: boolean }) => void;
  onChanged: () => void;
  onClose: () => void;
}

export function ScenePanel({ scene, looks, saving, onSave, onChanged, onClose }: ScenePanelProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(scene.text ?? '');
  const [visualPrompt, setVisualPrompt] = useState(scene.visualPrompt ?? '');
  const [durationSec, setDurationSec] = useState(String(scene.durationSec));
  const [type, setType] = useState<SceneType>(scene.type);
  const [lookId, setLookId] = useState<string | null>(scene.lookId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isGraphics = type === SceneType.MOTION_GRAPHICS;
  const spec = graphicsSpecSchema.safeParse(scene.graphics);
  const graphicsSpec: GraphicsSpec | null = spec.success ? spec.data : null;

  const describe = useMutation({
    mutationFn: (value: string) => projectsApi.describeGraphics(scene.id, value),
    onSuccess: onChanged,
  });

  // Титры подгружаем отдельно: в раскадровке они не нужны, а в панели
  // должны быть свежими — их пересобирает генерация сцены.
  const captions = useQuery({
    queryKey: ['scene-captions', scene.id],
    queryFn: () => projectsApi.getCaptions(scene.id),
    enabled: Boolean(scene.text?.trim()),
  });

  const refreshCaptions = () => {
    void queryClient.invalidateQueries({ queryKey: ['scene-captions', scene.id] });
    onChanged();
  };

  const saveCaptions = useMutation({
    mutationFn: (input: { enabled?: boolean; position?: string | null; lines?: CaptionLine[] }) =>
      projectsApi.updateCaptions(scene.id, input as never),
    onSuccess: refreshCaptions,
  });

  const rebuildCaptions = useMutation({
    mutationFn: () => projectsApi.regenerateCaptions(scene.id),
    onSuccess: refreshCaptions,
  });

  const textChanged = text !== (scene.text ?? '');
  const visualChanged = visualPrompt !== (scene.visualPrompt ?? '');
  const willRegenerate = scene.status === 'ready' && (textChanged || visualChanged);

  return (
    <aside className="flex w-full flex-col gap-5 rounded-card border border-line bg-surface p-5 lg:w-[360px]">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-card-title">{t('scene.title')}</p>
          <p className="text-meta text-content-muted">
            {t(`enum.sceneType.${scene.type}`, { defaultValue: scene.typeLabel })}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="rounded-control p-1 text-content-muted transition-colors duration-fast hover:text-content"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </div>

      {/* Сначала результат, потом правки: человек открывает сцену, чтобы
          посмотреть, что получилось, и только потом решает, что менять. */}
      <SceneResult scene={scene} onChanged={onChanged} />

      {/*
        §22: сцене не хватает материала — ведём туда, где его дают.

        Просто сказать «подготовьте персону» мало: человек не знает, где это
        делается, и ищет по разделам. Кнопка ведёт прямо на съёмку.
      */}
      {scene.missingRequirements.some((item) => item.code === 'scene.needsDynamicPrep') ? (
        <Alert tone="info">
          <div className="flex flex-col items-start gap-3">
            <span>{t('notice.scene.needsDynamicPrep')}</span>
            <Button onClick={() => navigate('/persona/dynamic')}>
              {t('persona.prepareDynamic')}
            </Button>
          </div>
        </Alert>
      ) : null}

      {/* У графики нет реплики и нет кадра — там графику описывают отдельно,
          и дублировать те же поля значит показывать один текст дважды. */}
      {isGraphics ? null : (
        <>
          <label className="flex flex-col gap-1.5">
            <span className="text-meta text-content-secondary">{t('scene.script')}</span>
            <textarea
              value={text}
              onChange={(event) => setText(event.target.value)}
              rows={4}
              placeholder={t('scene.scriptPlaceholder')}
              className="resize-none rounded-control border border-line bg-surface-secondary px-3 py-2.5 text-body text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-meta text-content-secondary">{t('scene.visual')}</span>
            <textarea
              value={visualPrompt}
              onChange={(event) => setVisualPrompt(event.target.value)}
              rows={3}
              placeholder={t('scene.visualPlaceholder')}
              className="resize-none rounded-control border border-line bg-surface-secondary px-3 py-2.5 text-body text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
            />
          </label>
        </>
      )}

      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-meta text-content-secondary">{t('scene.type')}</span>
          <select
            value={type}
            onChange={(event) => setType(event.target.value as SceneType)}
            className="h-10 rounded-control border border-line bg-surface-secondary px-3 text-body text-content focus:border-accent focus:outline-none"
          >
            {editableTypes.map((value) => (
              <option key={value} value={value}>
                {sceneTypeLabels[value]}
              </option>
            ))}
          </select>
        </label>

        <Input
          label={t('scene.duration')}
          type="number"
          min={1}
          max={30}
          step={0.5}
          value={durationSec}
          onChange={(event) => setDurationSec(event.target.value)}
        />
      </div>

      {type === SceneType.PERSONA_SPEECH || type === SceneType.DYNAMIC_PERSONA ? (
        <label className="flex flex-col gap-1.5">
          <span className="text-meta text-content-secondary">{t('create.look')}</span>
          <select
            value={lookId ?? ''}
            onChange={(event) => setLookId(event.target.value || null)}
            className="h-10 rounded-control border border-line bg-surface-secondary px-3 text-body text-content focus:border-accent focus:outline-none"
          >
            <option value="">Auto</option>
            {looks.map((look) => (
              <option key={look.id} value={look.id}>
                {look.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* §25: графика описывается словами, структура остаётся видимой */}
      {type === SceneType.MOTION_GRAPHICS ? (
        <GraphicsEditor
          description={scene.visualPrompt ?? ''}
          spec={graphicsSpec}
          busy={describe.isPending}
          onDescribe={(value) => describe.mutate(value)}
        />
      ) : null}

      {/* §28: титры */}
      {captions.data ? (
        <CaptionsEditor
          key={`${captions.data.source}-${captions.data.lines.length}-${captions.dataUpdatedAt}`}
          captions={captions.data}
          saving={saveCaptions.isPending || rebuildCaptions.isPending}
          onSave={(input) => saveCaptions.mutate(input)}
          onRegenerate={() => rebuildCaptions.mutate()}
        />
      ) : null}

      {/* §36: что именно придётся пересоздать и почём — до подтверждения */}
      {willRegenerate ? (
        <p className="rounded-control border border-line bg-surface-secondary px-3 py-2.5 text-meta text-content-secondary">
          {textChanged && visualChanged
            ? t('scene.changedBoth')
            : textChanged
              ? t('scene.changedScript')
              : t('scene.changedVisual')}
          : {t('scene.unitsCount', { count: scene.estimatedUnits })}
        </p>
      ) : null}

      <div className="flex gap-3">
        <Button
          variant="primary"
          disabled={saving}
          onClick={() =>
            onSave(
              {
                // Описание графики принадлежит своему редактору: отправив сюда
                // старое значение из скрытого поля, мы затёрли бы разбор §25.
                ...(isGraphics ? {} : { text, visualPrompt }),
                durationSec: Number(durationSec),
                type,
                lookId,
              },
              /**
               * Правка текста или кадра — это просьба пересоздать сцену.
               *
               * Раньше сохранение только возвращало сцену в черновик, и человек
               * оставался с прежним роликом, не понимая, почему ничего
               * не изменилось: кнопку пересоздания надо было искать в меню
               * карточки. Стоимость показана рядом заранее (§36), так что
               * нажатие осознанное.
               */
              { regenerate: willRegenerate },
            )
          }
        >
          {saving
            ? t('common.saving')
            : willRegenerate
              ? t('scene.saveAndRegenerate')
              : t('common.save')}
        </Button>
        <Button variant="tertiary" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </aside>
  );
}
